<div class="style-guide-page">
  <header class="style-guide-header">
    <h1>Style Guide</h1>
    <p>Consumer Portal UI component library and design tokens reference.</p>
    <nav class="gallery-nav">
      <a href="/screenshots" class="gallery-nav-link">Screenshot Gallery</a>
      <a href="/modal-gallery" class="gallery-nav-link">Modal Gallery</a>
    </nav>
  </header>

  <nav class="style-guide-nav">
    <a href="/style-guide#colors">Colors</a>
    <a href="/style-guide#typography">Typography</a>
    <a href="/style-guide#buttons">Buttons</a>
    <a href="/style-guide#cards">Cards</a>
    <a href="/style-guide#forms">Form Elements</a>
    <a href="/style-guide#loading">Loading States</a>
    <a href="/style-guide#tabs">Tabs</a>
    <a href="/style-guide#chips">Chips</a>
    <a href="/style-guide#expansion">Expansion Panels</a>
    <a href="/style-guide#links">Links</a>
    <a href="/style-guide#layout">Layout</a>
    <a href="/style-guide#toasts">Toasts</a>
    <a href="/style-guide#banners">Banners</a>
    <a href="/style-guide#tooltips">Tooltips</a>
    <a href="/style-guide#avatars">Avatars</a>
    <a href="/style-guide#dividers">Dividers</a>
    <a href="/style-guide#badges">Badges</a>
    <a href="/style-guide#icons">Icons</a>
    <a href="/style-guide#shadows">Shadows</a>
    <a href="/style-guide#radii">Border Radius</a>
    <a href="/style-guide#galleries">Related Galleries</a>
  </nav>

  <!-- ═══════════════════════════════════════════════════ -->
  <!-- COLORS                                              -->
  <!-- ═══════════════════════════════════════════════════ -->
  <section id="colors" class="sg-section">
    <h2 class="sg-section-title">Color Palette</h2>
    <p class="sg-section-desc">Brand, semantic, and neutral colors used across the Consumer Portal.</p>

    <h3 class="font-dm text-base font-semibold text-ld-neutral mb-3">Primary / Brand</h3>
    <div class="color-grid mb-6">
      @for (color of primaryColors; track color.name) {
        <div class="color-swatch">
          <div class="color-swatch-preview" [style.background-color]="color.value"></div>
          <div class="color-swatch-info">
            <span class="color-name">{{ color.name }}</span>
            <span class="color-value">{{ color.value }}</span>
            <span class="color-var">{{ color.cssVar }}</span>
          </div>
        </div>
      }
    </div>

    <h3 class="font-dm text-base font-semibold text-ld-neutral mb-3">Semantic</h3>
    <div class="color-grid mb-6">
      @for (color of semanticColors; track color.name) {
        <div class="color-swatch">
          <div class="color-swatch-preview" [style.background-color]="color.value"></div>
          <div class="color-swatch-info">
            <span class="color-name">{{ color.name }}</span>
            <span class="color-value">{{ color.value }}</span>
            <span class="color-var">{{ color.cssVar }}</span>
          </div>
        </div>
      }
    </div>

    <h3 class="font-dm text-base font-semibold text-ld-neutral mb-3">Neutrals</h3>
    <div class="color-grid mb-6">
      @for (color of neutralColors; track color.name) {
        <div class="color-swatch">
          <div class="color-swatch-preview" [style.background-color]="color.value"></div>
          <div class="color-swatch-info">
            <span class="color-name">{{ color.name }}</span>
            <span class="color-value">{{ color.value }}</span>
            <span class="color-var">{{ color.cssVar }}</span>
          </div>
        </div>
      }
    </div>

    <h3 class="font-dm text-base font-semibold text-ld-neutral mb-3">Accent</h3>
    <div class="color-grid">
      @for (color of accentColors; track color.name) {
        <div class="color-swatch">
          <div class="color-swatch-preview" [style.background-color]="color.value"></div>
          <div class="color-swatch-info">
            <span class="color-name">{{ color.name }}</span>
            <span class="color-value">{{ color.value }}</span>
            <span class="color-var">{{ color.cssVar }}</span>
          </div>
        </div>
      }
    </div>
  </section>

  <!-- ═══════════════════════════════════════════════════ -->
  <!-- TYPOGRAPHY                                          -->
  <!-- ═══════════════════════════════════════════════════ -->
  <section id="typography" class="sg-section">
    <h2 class="sg-section-title">Typography</h2>
    <p class="sg-section-desc">Text styles defined as reusable CSS component classes.</p>

    <div class="type-specimens">
      <div class="type-specimen">
        <div class="type-meta">
          <span class="type-class">.text-page-title</span>
          <span class="type-specs">DM Sans / 24px / 600 / 2rem</span>
        </div>
        <span class="text-page-title">Page Title</span>
      </div>

      <div class="type-specimen">
        <div class="type-meta">
          <span class="type-class">.text-section-heading</span>
          <span class="type-specs">DM Sans / 20px / 600 / 1.75rem</span>
        </div>
        <span class="text-section-heading">Section Heading</span>
      </div>

      <div class="type-specimen">
        <div class="type-meta">
          <span class="type-class">.text-card-title</span>
          <span class="type-specs">DM Sans / 16px / 600 / 1.5rem</span>
        </div>
        <span class="text-card-title">Card Title</span>
      </div>

      <div class="type-specimen">
        <div class="type-meta">
          <span class="type-class">.text-body</span>
          <span class="type-specs">DM Sans / 14px / 400 / 1.25rem</span>
        </div>
        <span class="text-body">Body text used for general content throughout the application.</span>
      </div>

      <div class="type-specimen">
        <div class="type-meta">
          <span class="type-class">.text-caption</span>
          <span class="type-specs">DM Sans / 12px / 400 / 1rem</span>
        </div>
        <span class="text-caption">Caption text for secondary information and labels.</span>
      </div>

      <div class="type-specimen">
        <div class="type-meta">
          <span class="type-class">.text-display-amount</span>
          <span class="type-specs">DM Sans / 32px / 500 / 2.5rem</span>
        </div>
        <span class="text-display-amount">$1,525.43</span>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════════════════════════════════ -->
  <!-- BUTTONS                                             -->
  <!-- ═══════════════════════════════════════════════════ -->
  <section id="buttons" class="sg-section">
    <h2 class="sg-section-title">Buttons</h2>
    <p class="sg-section-desc">
      All button variants with their interactive states. Disabled buttons retain their background color at 50% opacity.
    </p>

    <div class="button-grid">
      <!-- Primary Purple -->
      <div class="button-card">
        <span class="button-card-title">.btn-primary-ld-purple</span>
        <div class="button-states">
          <div class="button-state-row">
            <span class="state-label">Default</span>
            <button type="button" class="btn-primary-ld-purple">Submit</button>
          </div>
          <div class="button-state-row">
            <span class="state-label">Disabled</span>
            <button type="button" class="btn-primary-ld-purple" disabled>Submit</button>
          </div>
        </div>
      </div>

      <!-- Outline Purple -->
      <div class="button-card">
        <span class="button-card-title">.btn-outline-ld-purple</span>
        <div class="button-states">
          <div class="button-state-row">
            <span class="state-label">Default</span>
            <button type="button" class="btn-outline-ld-purple">Cancel</button>
          </div>
          <div class="button-state-row">
            <span class="state-label">Disabled</span>
            <button type="button" class="btn-outline-ld-purple" disabled>Cancel</button>
          </div>
        </div>
      </div>

      <!-- Primary Neutral -->
      <div class="button-card">
        <span class="button-card-title">.btn-primary-ld-neutral</span>
        <div class="button-states">
          <div class="button-state-row">
            <span class="state-label">Default</span>
            <button type="button" class="btn-primary-ld-neutral">Continue</button>
          </div>
          <div class="button-state-row">
            <span class="state-label">Disabled</span>
            <button type="button" class="btn-primary-ld-neutral" disabled>Continue</button>
          </div>
        </div>
      </div>

      <!-- Solid Red -->
      <div class="button-card">
        <span class="button-card-title">.btn-solid-red</span>
        <div class="button-states">
          <div class="button-state-row">
            <span class="state-label">Default</span>
            <button type="button" class="btn-solid-red">Delete</button>
          </div>
          <div class="button-state-row">
            <span class="state-label">Disabled</span>
            <button type="button" class="btn-solid-red" disabled>Delete</button>
          </div>
        </div>
      </div>

      <!-- Primary JV -->
      <div class="button-card">
        <span class="button-card-title">.btn-primary-jv</span>
        <div class="button-states">
          <div class="button-state-row">
            <span class="state-label">Default</span>
            <button type="button" class="btn-primary-jv">Get Started</button>
          </div>
          <div class="button-state-row">
            <span class="state-label">Disabled</span>
            <button type="button" class="btn-primary-jv" disabled>Get Started</button>
          </div>
        </div>
      </div>

      <!-- Outline JV -->
      <div class="button-card">
        <span class="button-card-title">.btn-outline-jv</span>
        <div class="button-states">
          <div class="button-state-row">
            <span class="state-label">Default</span>
            <button type="button" class="btn-outline-jv">Learn More</button>
          </div>
          <div class="button-state-row">
            <span class="state-label">Disabled</span>
            <button type="button" class="btn-outline-jv" disabled>Learn More</button>
          </div>
        </div>
      </div>

      <!-- Disabled class -->
      <div class="button-card">
        <span class="button-card-title">.btn-disabled</span>
        <div class="button-states">
          <div class="button-state-row">
            <span class="state-label">Static</span>
            <span class="btn-disabled">Unavailable</span>
          </div>
        </div>
      </div>
    </div>

    <h3 class="font-dm text-base font-semibold text-ld-neutral mt-8 mb-3">Button Pair (Primary + Outline)</h3>
    <p class="sg-section-desc">
      Buttons reorder on mobile (primary on top) vs desktop (primary on right) via CSS order.
    </p>
    <div class="button-card" style="max-width: 500px">
      <div class="flex flex-col sm:flex-row gap-3">
        <button type="button" class="btn-outline-ld-purple">Cancel</button>
        <button type="button" class="btn-primary-ld-purple">Confirm</button>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════════════════════════════════ -->
  <!-- CARDS                                               -->
  <!-- ═══════════════════════════════════════════════════ -->
  <section id="cards" class="sg-section">
    <h2 class="sg-section-title">Cards</h2>
    <p class="sg-section-desc">Card container styles for content grouping.</p>

    <div class="card-grid">
      <div class="card-demo-wrapper">
        <span class="card-demo-label">.card-base</span>
        <div class="card-base">
          <h3 class="text-card-title mb-2">Card Base</h3>
          <p class="text-body">
            Standard card with 1.5rem padding (2rem on desktop), 1.5rem border radius, and subtle box shadow.
          </p>
        </div>
      </div>

      <div class="card-demo-wrapper">
        <span class="card-demo-label">.card-base-sm</span>
        <div class="card-base-sm">
          <h3 class="text-card-title mb-2">Card Base Small</h3>
          <p class="text-body">
            Compact card with 1rem padding (1.5rem on desktop), 1rem border radius, and subtle box shadow.
          </p>
        </div>
      </div>

      <div class="card-demo-wrapper">
        <span class="card-demo-label">.card-shell</span>
        <div class="card-shell" style="padding: 0">
          <div style="background: #501283; padding: 1rem 1.5rem">
            <h3 class="text-card-title" style="color: white">Card Shell Header</h3>
          </div>
          <div style="padding: 1.5rem">
            <p class="text-body">
              Shell card with no padding and overflow hidden — designed for cards with full-bleed headers or images.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════════════════════════════════ -->
  <!-- FORM ELEMENTS                                       -->
  <!-- ═══════════════════════════════════════════════════ -->
  <section id="forms" class="sg-section">
    <h2 class="sg-section-title">Form Elements</h2>
    <p class="sg-section-desc">Common form input patterns using Angular Material with custom theming.</p>

    <div class="form-grid">
      <div class="form-card">
        <span class="form-card-title">Text Input</span>
        <div class="form-demo">
          <mat-form-field appearance="outline" class="w-full profile-field">
            <mat-label>Email Address</mat-label>
            <input matInput placeholder="user@example.com" value="john.doe@loandepot.com" />
          </mat-form-field>
          <mat-form-field appearance="outline" class="w-full profile-field">
            <mat-label>Disabled Input</mat-label>
            <input matInput placeholder="Cannot edit" disabled value="Read-only value" />
          </mat-form-field>
        </div>
      </div>

      <div class="form-card">
        <span class="form-card-title">Select / Dropdown</span>
        <div class="form-demo">
          <mat-form-field appearance="outline" class="w-full profile-field">
            <mat-label>Loan Type</mat-label>
            <mat-select value="conventional">
              <mat-option value="conventional">Conventional</mat-option>
              <mat-option value="fha">FHA</mat-option>
              <mat-option value="va">VA</mat-option>
              <mat-option value="heloc">HELOC</mat-option>
            </mat-select>
          </mat-form-field>
        </div>
      </div>

      <div class="form-card">
        <span class="form-card-title">Checkbox &amp; Toggle</span>
        <div class="form-demo">
          <mat-checkbox [checked]="true" color="primary">I agree to the terms</mat-checkbox>
          <mat-checkbox color="primary">Subscribe to email updates</mat-checkbox>
          <mat-checkbox [disabled]="true" color="primary">Disabled checkbox</mat-checkbox>
          <div class="mt-2">
            <mat-slide-toggle color="primary">Paperless billing</mat-slide-toggle>
          </div>
          <div>
            <mat-slide-toggle color="primary" [disabled]="true">Disabled toggle</mat-slide-toggle>
          </div>
        </div>
      </div>

      <div class="form-card">
        <span class="form-card-title">Radio Buttons</span>
        <div class="form-demo">
          <mat-radio-group color="primary" value="monthly">
            <div class="flex flex-col gap-2">
              <mat-radio-button value="monthly">Monthly</mat-radio-button>
              <mat-radio-button value="biweekly">Bi-weekly</mat-radio-button>
              <mat-radio-button value="custom" [disabled]="true">Custom (unavailable)</mat-radio-button>
            </div>
          </mat-radio-group>
        </div>
      </div>

      <div class="form-card">
        <span class="form-card-title">Date Picker</span>
        <div class="form-demo">
          <mat-form-field appearance="outline" class="w-full profile-field">
            <mat-label>Payment Date</mat-label>
            <input matInput [matDatepicker]="picker" />
            <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
            <mat-datepicker #picker></mat-datepicker>
          </mat-form-field>
        </div>
      </div>

      <div class="form-card">
        <span class="form-card-title">Textarea</span>
        <div class="form-demo">
          <mat-form-field appearance="outline" class="w-full mat-form-field-legacy mat-form-field-textarea">
            <mat-label>Message</mat-label>
            <textarea matInput rows="3" placeholder="Type your message here..."></textarea>
          </mat-form-field>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════════════════════════════════ -->
  <!-- LOADING STATES                                      -->
  <!-- ═══════════════════════════════════════════════════ -->
  <section id="loading" class="sg-section">
    <h2 class="sg-section-title">Loading States</h2>
    <p class="sg-section-desc">Spinners, progress bars, and skeleton animations for asynchronous content.</p>

    <div class="loading-grid">
      <div class="loading-card">
        <span class="loading-card-title">Spinner (Default)</span>
        <p class="text-caption mb-3">Purple brand spinner — used for page-level and card-level loading.</p>
        <div class="loading-demo">
          <mat-spinner diameter="48"></mat-spinner>
        </div>
      </div>

      <div class="loading-card">
        <span class="loading-card-title">Spinner (Small / In-Button)</span>
        <p class="text-caption mb-3">16–20px spinners embedded inside buttons during submission.</p>
        <div class="loading-demo">
          <div class="flex items-center gap-4">
            <mat-spinner diameter="16" class="w-4! h-4!"></mat-spinner>
            <mat-spinner diameter="20" class="w-5! h-5!"></mat-spinner>
            <button type="button" class="btn-primary-ld-purple" disabled>
              <mat-spinner diameter="16" class="w-4! h-4! [&_circle]:stroke-white!"></mat-spinner>
              Submitting...
            </button>
          </div>
        </div>
      </div>

      <div class="loading-card">
        <span class="loading-card-title">Progress Bar</span>
        <p class="text-caption mb-3">Determinate progress bar for uploads and multi-step processes.</p>
        <div class="loading-demo">
          <mat-progress-bar mode="determinate" [value]="65" class="rounded-full"></mat-progress-bar>
          <div class="mt-2">
            <mat-progress-bar mode="indeterminate" class="rounded-full"></mat-progress-bar>
          </div>
        </div>
      </div>

      <div class="loading-card">
        <span class="loading-card-title">Skeleton / Shimmer</span>
        <p class="text-caption mb-3">
          Placeholder animation while content loads. Apply
          <code>.animate-shimmer</code>
          to any element.
        </p>
        <div class="loading-demo">
          <div class="flex flex-col gap-3">
            <div class="animate-shimmer" style="height: 16px; width: 60%; border-radius: 4px"></div>
            <div class="animate-shimmer" style="height: 12px; width: 80%; border-radius: 4px"></div>
            <div class="animate-shimmer" style="height: 12px; width: 45%; border-radius: 4px"></div>
            <div class="animate-shimmer" style="height: 48px; width: 100%; border-radius: 12px; margin-top: 8px"></div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════════════════════════════════ -->
  <!-- TABS                                                -->
  <!-- ═══════════════════════════════════════════════════ -->
  <section id="tabs" class="sg-section">
    <h2 class="sg-section-title">Tabs</h2>
    <p class="sg-section-desc">
      Angular Material tabs with custom LD purple theming. Used for dashboard, account details, and payment views.
    </p>

    <div class="tab-demo-card">
      <mat-tab-group>
        <mat-tab label="Overview">
          <div class="tab-content-demo">
            <p class="text-body">
              Overview tab content. Active tab uses
              <strong>ld-primary</strong>
              underline and text color.
            </p>
          </div>
        </mat-tab>
        <mat-tab label="Payment History">
          <div class="tab-content-demo">
            <p class="text-body">Payment history tab content.</p>
          </div>
        </mat-tab>
        <mat-tab label="Documents">
          <div class="tab-content-demo">
            <p class="text-body">Documents tab content.</p>
          </div>
        </mat-tab>
      </mat-tab-group>
    </div>
  </section>

  <!-- ═══════════════════════════════════════════════════ -->
  <!-- CHIPS                                               -->
  <!-- ═══════════════════════════════════════════════════ -->
  <section id="chips" class="sg-section">
    <h2 class="sg-section-title">Chips</h2>
    <p class="sg-section-desc">Status chips and tags used for loan status, payment status, and categorization.</p>

    <div class="chip-demo-card">
      <div class="flex flex-wrap gap-3">
        <mat-chip-set>
          <mat-chip class="!bg-ld-jade-50 !text-ld-jade-600">Approved</mat-chip>
          <mat-chip class="!bg-ld-warning-50 !text-amber-700">Pending</mat-chip>
          <mat-chip class="!bg-ld-error-light !text-ld-error">Declined</mat-chip>
          <mat-chip class="!bg-ld-primary-50 !text-ld-primary">In Progress</mat-chip>
          <mat-chip class="!bg-ld-neutral-100 !text-ld-neutral-500">Inactive</mat-chip>
        </mat-chip-set>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════════════════════════════════ -->
  <!-- EXPANSION PANELS                                    -->
  <!-- ═══════════════════════════════════════════════════ -->
  <section id="expansion" class="sg-section">
    <h2 class="sg-section-title">Expansion Panels</h2>
    <p class="sg-section-desc">Collapsible content sections used for FAQs, account details, and review pages.</p>

    <div class="expansion-demo-card">
      <mat-accordion>
        <mat-expansion-panel>
          <mat-expansion-panel-header>
            <mat-panel-title class="text-card-title">What is my monthly payment?</mat-panel-title>
          </mat-expansion-panel-header>
          <p class="text-body">
            Your monthly payment includes principal, interest, taxes, and insurance (PITI). Check the Account Details
            page for a full breakdown.
          </p>
        </mat-expansion-panel>
        <mat-expansion-panel>
          <mat-expansion-panel-header>
            <mat-panel-title class="text-card-title">How do I set up autopay?</mat-panel-title>
          </mat-expansion-panel-header>
          <p class="text-body">
            Navigate to Payments &gt; Autopay to enroll. You&#39;ll need a linked bank account and must agree to the
            autopay consent terms.
          </p>
        </mat-expansion-panel>
        <mat-expansion-panel disabled>
          <mat-expansion-panel-header>
            <mat-panel-title class="text-card-title">Disabled panel example</mat-panel-title>
          </mat-expansion-panel-header>
          <p class="text-body">This content is not accessible.</p>
        </mat-expansion-panel>
      </mat-accordion>
    </div>
  </section>

  <!-- ═══════════════════════════════════════════════════ -->
  <!-- LINKS                                               -->
  <!-- ═══════════════════════════════════════════════════ -->
  <section id="links" class="sg-section">
    <h2 class="sg-section-title">Links</h2>
    <p class="sg-section-desc">Standard link colors and styles for inline and standalone text links.</p>

    <div class="link-demo-card">
      <div class="flex flex-col gap-4">
        <div>
          <span class="link-demo-label">Inline link</span>
          <p class="text-body">
            For questions,
            <a href="javascript:void(0)" class="text-ld-link hover:text-ld-link-hover underline">
              contact your loan officer
            </a>
            or visit our help center.
          </p>
        </div>
        <div>
          <span class="link-demo-label">Standalone link</span>
          <a
            href="javascript:void(0)"
            class="text-ld-link hover:text-ld-link-hover underline font-dm text-sm font-semibold"
          >
            View all payment history →
          </a>
        </div>
        <div>
          <span class="link-demo-label">Color tokens</span>
          <div class="flex gap-4 mt-1">
            <div class="flex items-center gap-2">
              <div style="width: 16px; height: 16px; background: #0d6bb8; border-radius: 4px"></div>
              <span class="text-caption">--color-ld-link (#0d6bb8)</span>
            </div>
            <div class="flex items-center gap-2">
              <div style="width: 16px; height: 16px; background: #0a5a9e; border-radius: 4px"></div>
              <span class="text-caption">--color-ld-link-hover (#0a5a9e)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════════════════════════════════ -->
  <!-- LAYOUT UTILITIES                                    -->
  <!-- ═══════════════════════════════════════════════════ -->
  <section id="layout" class="sg-section">
    <h2 class="sg-section-title">Layout Utilities</h2>
    <p class="sg-section-desc">Reusable layout classes for page structure and content containment.</p>

    <div class="layout-grid">
      <div class="layout-card">
        <span class="layout-card-title">.page-container</span>
        <p class="text-caption mb-2">
          Centers content with max-width of
          <code>--max-width-page</code>
          (1328px) and responsive horizontal margins.
        </p>
        <div class="layout-preview">
          <div class="layout-preview-bar" style="width: 100%">
            <span>page-container</span>
          </div>
        </div>
      </div>

      <div class="layout-card">
        <span class="layout-card-title">.page-content-padding</span>
        <p class="text-caption mb-2">Responsive horizontal padding: 1rem mobile → 1.5rem tablet → 0 desktop.</p>
        <div class="layout-preview">
          <div class="layout-preview-bar" style="width: 80%">
            <span>page-content-padding</span>
          </div>
        </div>
      </div>

      <div class="layout-card">
        <span class="layout-card-title">.icon-purple</span>
        <p class="text-caption mb-2">CSS filter that recolors any image/icon to ld-primary purple.</p>
        <div class="flex items-center gap-4">
          <img
            src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'%3E%3C/path%3E%3Cpolyline points='9 22 9 12 15 12 15 22'%3E%3C/polyline%3E%3C/svg%3E"
            alt="Home icon (original)"
          />
          <span class="text-caption">→</span>
          <img
            src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'%3E%3C/path%3E%3Cpolyline points='9 22 9 12 15 12 15 22'%3E%3C/polyline%3E%3C/svg%3E"
            alt="Home icon (purple)"
            class="icon-purple"
          />
          <span class="text-caption">.icon-purple applied</span>
        </div>
      </div>

      <div class="layout-card">
        <span class="layout-card-title">.touch-target</span>
        <p class="text-caption mb-2">
          Ensures minimum 44×44px tap area for WCAG AA compliance on interactive elements.
        </p>
        <div class="flex items-center gap-4">
          <div
            class="touch-target flex items-center justify-center border border-dashed border-ld-neutral-200 rounded-lg"
          >
            <span class="text-caption">44×44</span>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════════════════════════════════ -->
  <!-- TOASTS / NOTIFICATIONS                              -->
  <!-- ═══════════════════════════════════════════════════ -->
  <section id="toasts" class="sg-section">
    <h2 class="sg-section-title">Toasts / Notifications</h2>
    <p class="sg-section-desc">
      Toast notifications for transient feedback. Four semantic types with auto-dismiss. Used via
      <code>ToastService</code>
      .
    </p>

    <div class="toast-demo-grid">
      <div class="toast-demo-card">
        <span class="toast-demo-label">Success</span>
        <div class="toast-demo-item bg-white text-ld-success border-2 border-ld-success shadow-2xl">
          <div class="shrink-0 mt-0.5">
            <mat-icon class="w-5! h-5! text-[20px]!">check_circle</mat-icon>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold font-dm">Payment Submitted</p>
            <p class="text-sm font-dm mt-1">Your payment of $1,525.43 has been submitted successfully.</p>
          </div>
        </div>
      </div>

      <div class="toast-demo-card">
        <span class="toast-demo-label">Error</span>
        <div class="toast-demo-item bg-white text-ld-error border-2 border-ld-error shadow-2xl">
          <div class="shrink-0 mt-0.5">
            <mat-icon class="w-5! h-5! text-[20px]!">error</mat-icon>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold font-dm">Upload Failed</p>
            <p class="text-sm font-dm mt-1">The file could not be uploaded. Please try again.</p>
          </div>
        </div>
      </div>

      <div class="toast-demo-card">
        <span class="toast-demo-label">Warning</span>
        <div class="toast-demo-item bg-white text-ld-warning border-2 border-ld-warning shadow-2xl">
          <div class="shrink-0 mt-0.5">
            <mat-icon class="w-5! h-5! text-[20px]!">warning</mat-icon>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold font-dm">Session Expiring</p>
            <p class="text-sm font-dm mt-1">Your session will expire in 5 minutes.</p>
          </div>
        </div>
      </div>

      <div class="toast-demo-card">
        <span class="toast-demo-label">Info</span>
        <div class="toast-demo-item bg-white text-ld-link border-2 border-ld-link shadow-2xl">
          <div class="shrink-0 mt-0.5">
            <mat-icon class="w-5! h-5! text-[20px]!">info</mat-icon>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold font-dm">Document Ready</p>
            <p class="text-sm font-dm mt-1">Your tax document is now available for download.</p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════════════════════════════════ -->
  <!-- BANNERS / ALERTS                                    -->
  <!-- ═══════════════════════════════════════════════════ -->
  <section id="banners" class="sg-section">
    <h2 class="sg-section-title">Banners &amp; Alerts</h2>
    <p class="sg-section-desc">
      Persistent alert banners for system messages, warnings, and announcements. Used via
      <code>ld-dynamic-message-banner</code>
      and
      <code>ld-announcement-bar</code>
      .
    </p>

    <div class="banner-demo-stack">
      <div class="banner-demo-card">
        <span class="banner-demo-label">Announcement Bar (Warning)</span>
        <div
          class="w-full border rounded-full px-6 py-3 font-dm bg-ld-warning-50 border-ld-warning flex items-center gap-3"
          role="alert"
        >
          <mat-icon class="w-5! h-5! text-[20px]! shrink-0 text-ld-warning" aria-hidden="true">warning</mat-icon>
          <p class="text-sm text-ld-neutral leading-5 m-0">
            <span class="font-bold">We're here to help</span>
            <span class="font-bold">–</span>
            <span>If you've been affected by a recent disaster, we have resources available to assist you.</span>
          </p>
        </div>
      </div>

      <div class="banner-demo-card">
        <span class="banner-demo-label">Info Banner</span>
        <div
          class="w-full border rounded-[24px] px-6 py-4 font-dm bg-ld-primary-50 border-ld-primary-100 flex items-center gap-4"
          role="alert"
        >
          <mat-icon class="w-6! h-6! text-[24px]! shrink-0 text-ld-primary" aria-hidden="true">info</mat-icon>
          <p class="text-base leading-6 m-0 text-ld-neutral-700">
            <span class="font-bold">Paperless is now available</span>
            <span class="font-bold">–</span>
            <span>Enroll today to receive your statements electronically.</span>
          </p>
        </div>
      </div>

      <div class="banner-demo-card">
        <span class="banner-demo-label">Error / Alert Banner</span>
        <div
          class="w-full border rounded-[24px] px-6 py-4 font-dm bg-ld-error-light border-ld-error flex items-center gap-4"
          role="alert"
        >
          <mat-icon class="w-6! h-6! text-[24px]! shrink-0 text-ld-error" aria-hidden="true">error</mat-icon>
          <p class="text-base leading-6 m-0 text-ld-neutral-700">
            <span class="font-bold">Payment past due</span>
            <span class="font-bold">–</span>
            <span>Your account has an overdue balance. Please make a payment to avoid late fees.</span>
          </p>
        </div>
      </div>

      <div class="banner-demo-card">
        <span class="banner-demo-label">Transfer Banner (Card Style)</span>
        <div class="bg-amber-50 border border-amber-300 rounded-xl p-6" role="alert">
          <div class="flex items-start gap-4">
            <mat-icon class="text-amber-500! shrink-0 mt-0.5">warning</mat-icon>
            <div class="flex-1">
              <h3 class="font-dm font-bold text-base text-ld-neutral mb-3">Important: Servicing Transfer</h3>
              <p class="font-dm text-sm text-ld-neutral-700 mb-3">
                Your loan servicing will transfer to New Servicer on January 1, 2027. Your payment should be sent to the
                new servicer after that date.
              </p>
              <p class="font-dm text-sm text-ld-neutral-700 m-0">
                For questions, contact New Servicer at (800) 555-0199.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════════════════════════════════ -->
  <!-- TOOLTIPS                                            -->
  <!-- ═══════════════════════════════════════════════════ -->
  <section id="tooltips" class="sg-section">
    <h2 class="sg-section-title">Tooltips</h2>
    <p class="sg-section-desc">
      Angular Material tooltips for contextual help. Hover or focus to reveal. Used on info icons across forms and
      payment screens.
    </p>

    <div class="tooltip-demo-card">
      <div class="flex flex-wrap gap-6 items-center">
        <div class="flex items-center gap-2">
          <span class="text-body">Soft credit check</span>
          <mat-icon
            class="w-5! h-5! text-[20px]! text-ld-neutral-400 cursor-help"
            matTooltip="A soft credit check will not affect your credit score."
            matTooltipPosition="above"
          >
            help_outline
          </mat-icon>
        </div>

        <div class="flex items-center gap-2">
          <span class="text-body">SSN</span>
          <mat-icon
            class="w-5! h-5! text-[20px]! text-ld-neutral-400 cursor-help"
            matTooltip="Your Social Security Number is used for identity verification only."
            matTooltipPosition="right"
          >
            help_outline
          </mat-icon>
        </div>

        <div class="flex items-center gap-2">
          <span class="text-body">Payment amount</span>
          <mat-icon
            class="w-5! h-5! text-[20px]! text-ld-neutral-400 cursor-help"
            matTooltip="This includes principal, interest, taxes, and insurance."
            matTooltipPosition="below"
          >
            info_outline
          </mat-icon>
        </div>

        <button
          type="button"
          class="btn-primary-ld-purple"
          matTooltip="Button tooltips work too"
          matTooltipPosition="above"
        >
          Hover Me
        </button>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════════════════════════════════ -->
  <!-- AVATARS                                             -->
  <!-- ═══════════════════════════════════════════════════ -->
  <section id="avatars" class="sg-section">
    <h2 class="sg-section-title">Avatars</h2>
    <p class="sg-section-desc">
      Circular avatar patterns used for loan officers, borrower profiles, and contact cards. Includes image and initials
      fallback variants.
    </p>

    <div class="avatar-demo-card">
      <div class="flex flex-wrap gap-8 items-end">
        <div class="flex flex-col items-center gap-2">
          <div class="avatar-circle avatar-lg bg-ld-primary text-white">
            <span class="font-dm font-bold text-xl">JD</span>
          </div>
          <span class="text-caption">Large (64px)</span>
        </div>

        <div class="flex flex-col items-center gap-2">
          <div class="avatar-circle avatar-md bg-ld-primary text-white">
            <span class="font-dm font-bold text-base">JD</span>
          </div>
          <span class="text-caption">Medium (48px)</span>
        </div>

        <div class="flex flex-col items-center gap-2">
          <div class="avatar-circle avatar-sm bg-ld-primary text-white">
            <span class="font-dm font-bold text-xs">JD</span>
          </div>
          <span class="text-caption">Small (32px)</span>
        </div>

        <div class="flex flex-col items-center gap-2">
          <div class="avatar-circle avatar-lg bg-ld-neutral-100 border-2 border-ld-neutral-200 overflow-hidden">
            <mat-icon class="w-8! h-8! text-[32px]! text-ld-neutral-400">person</mat-icon>
          </div>
          <span class="text-caption">Default fallback</span>
        </div>

        <div class="flex flex-col items-center gap-2">
          <div class="avatar-circle avatar-lg bg-ld-jade text-white">
            <span class="font-dm font-bold text-xl">LO</span>
          </div>
          <span class="text-caption">Loan Officer</span>
        </div>

        <div class="flex flex-col items-center gap-2">
          <div class="avatar-ring">
            <div class="avatar-circle avatar-lg bg-ld-primary-50 text-ld-primary">
              <span class="font-dm font-bold text-xl">AB</span>
            </div>
          </div>
          <span class="text-caption">With ring</span>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════════════════════════════════ -->
  <!-- DIVIDERS                                            -->
  <!-- ═══════════════════════════════════════════════════ -->
  <section id="dividers" class="sg-section">
    <h2 class="sg-section-title">Dividers</h2>
    <p class="sg-section-desc">
      Horizontal and vertical dividers for separating content blocks. Uses Angular Material
      <code>mat-divider</code>
      and Tailwind border utilities.
    </p>

    <div class="divider-demo-card">
      <div class="flex flex-col gap-6">
        <div>
          <span class="divider-demo-label">mat-divider (default)</span>
          <div class="py-2">
            <p class="text-body mb-3">Content above the divider</p>
            <mat-divider></mat-divider>
            <p class="text-body mt-3">Content below the divider</p>
          </div>
        </div>

        <div>
          <span class="divider-demo-label">Tailwind border-b</span>
          <div class="py-2">
            <p class="text-body mb-3">Content above</p>
            <div class="border-b border-ld-border"></div>
            <p class="text-body mt-3">Content below</p>
          </div>
        </div>

        <div>
          <span class="divider-demo-label">Vertical divider (inline)</span>
          <div class="flex items-center gap-4 py-2">
            <span class="text-body">Item A</span>
            <div class="h-5 border-l border-ld-neutral-200"></div>
            <span class="text-body">Item B</span>
            <div class="h-5 border-l border-ld-neutral-200"></div>
            <span class="text-body">Item C</span>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════════════════════════════════ -->
  <!-- BADGES / STATUS INDICATORS                          -->
  <!-- ═══════════════════════════════════════════════════ -->
  <section id="badges" class="sg-section">
    <h2 class="sg-section-title">Badges &amp; Status Indicators</h2>
    <p class="sg-section-desc">
      Inline status indicators and badge patterns used for loan status, payment states, and document processing.
    </p>

    <div class="badge-demo-card">
      <h3 class="font-dm text-base font-semibold text-ld-neutral mb-4">Dot + Text Status</h3>
      <div class="flex flex-wrap gap-6 mb-6">
        <div class="flex items-center gap-2">
          <div class="w-2.5 h-2.5 rounded-full bg-ld-success"></div>
          <span class="font-dm text-sm text-ld-neutral">Current</span>
        </div>
        <div class="flex items-center gap-2">
          <div class="w-2.5 h-2.5 rounded-full bg-ld-warning"></div>
          <span class="font-dm text-sm text-ld-neutral">Pending</span>
        </div>
        <div class="flex items-center gap-2">
          <div class="w-2.5 h-2.5 rounded-full bg-ld-error"></div>
          <span class="font-dm text-sm text-ld-neutral">Past Due</span>
        </div>
        <div class="flex items-center gap-2">
          <div class="w-2.5 h-2.5 rounded-full bg-ld-primary"></div>
          <span class="font-dm text-sm text-ld-neutral">In Progress</span>
        </div>
        <div class="flex items-center gap-2">
          <div class="w-2.5 h-2.5 rounded-full bg-ld-neutral-200"></div>
          <span class="font-dm text-sm text-ld-neutral">Inactive</span>
        </div>
      </div>

      <h3 class="font-dm text-base font-semibold text-ld-neutral mb-4">Pill Badges</h3>
      <div class="flex flex-wrap gap-3 mb-6">
        <span
          class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold font-dm uppercase tracking-wide bg-ld-jade-50 text-ld-jade-600"
        >
          Approved
        </span>
        <span
          class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold font-dm uppercase tracking-wide bg-ld-warning-50 text-amber-700"
        >
          Under Review
        </span>
        <span
          class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold font-dm uppercase tracking-wide bg-ld-error-light text-ld-error"
        >
          Rejected
        </span>
        <span
          class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold font-dm uppercase tracking-wide bg-ld-primary-50 text-ld-primary"
        >
          Processing
        </span>
        <span
          class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold font-dm uppercase tracking-wide bg-ld-neutral-100 text-ld-neutral-500"
        >
          Closed
        </span>
      </div>

      <h3 class="font-dm text-base font-semibold text-ld-neutral mb-4">Icon + Text Status</h3>
      <div class="flex flex-wrap gap-6">
        <div class="flex items-center gap-1.5">
          <mat-icon class="w-4! h-4! text-[16px]! text-ld-success">check_circle</mat-icon>
          <span class="font-dm text-sm text-ld-success font-semibold">Submitted</span>
        </div>
        <div class="flex items-center gap-1.5">
          <mat-icon class="w-4! h-4! text-[16px]! text-ld-warning">schedule</mat-icon>
          <span class="font-dm text-sm text-amber-700 font-semibold">Awaiting Review</span>
        </div>
        <div class="flex items-center gap-1.5">
          <mat-icon class="w-4! h-4! text-[16px]! text-ld-error">cancel</mat-icon>
          <span class="font-dm text-sm text-ld-error font-semibold">Rejected</span>
        </div>
        <div class="flex items-center gap-1.5">
          <mat-icon class="w-4! h-4! text-[16px]! text-ld-link">file_download</mat-icon>
          <span class="font-dm text-sm text-ld-link font-semibold">Download Ready</span>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════════════════════════════════ -->
  <!-- ICONS                                               -->
  <!-- ═══════════════════════════════════════════════════ -->
  <section id="icons" class="sg-section">
    <h2 class="sg-section-title">Icons</h2>
    <p class="sg-section-desc">
      Material Icons (via
      <code>mat-icon</code>
      ) and custom SVG icons (via
      <code>ld-svg-icon</code>
      ). Material icons use both filled and outlined font sets.
    </p>

    <div class="icon-demo-grid">
      <div class="icon-demo-card">
        <span class="icon-demo-card-title">Common UI Icons</span>
        <div class="flex flex-wrap gap-4 mt-3">
          <div class="icon-demo-item">
            <mat-icon>home</mat-icon>
            <span>home</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon>account_circle</mat-icon>
            <span>account_circle</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon>payments</mat-icon>
            <span>payments</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon>description</mat-icon>
            <span>description</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon>chat</mat-icon>
            <span>chat</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon>settings</mat-icon>
            <span>settings</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon>notifications</mat-icon>
            <span>notifications</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon>logout</mat-icon>
            <span>logout</span>
          </div>
        </div>
      </div>

      <div class="icon-demo-card">
        <span class="icon-demo-card-title">Action Icons</span>
        <div class="flex flex-wrap gap-4 mt-3">
          <div class="icon-demo-item">
            <mat-icon>close</mat-icon>
            <span>close</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon>chevron_left</mat-icon>
            <span>chevron_left</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon>chevron_right</mat-icon>
            <span>chevron_right</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon>expand_more</mat-icon>
            <span>expand_more</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon>file_upload</mat-icon>
            <span>file_upload</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon>file_download</mat-icon>
            <span>file_download</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon>edit</mat-icon>
            <span>edit</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon>delete</mat-icon>
            <span>delete</span>
          </div>
        </div>
      </div>

      <div class="icon-demo-card">
        <span class="icon-demo-card-title">Status &amp; Feedback Icons</span>
        <div class="flex flex-wrap gap-4 mt-3">
          <div class="icon-demo-item">
            <mat-icon class="text-ld-success!">check_circle</mat-icon>
            <span>check_circle</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon class="text-ld-error!">error</mat-icon>
            <span>error</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon class="text-ld-warning!">warning</mat-icon>
            <span>warning</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon class="text-ld-link!">info</mat-icon>
            <span>info</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon fontSet="material-icons-outlined">help_outline</mat-icon>
            <span>help_outline</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon>visibility</mat-icon>
            <span>visibility</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon>visibility_off</mat-icon>
            <span>visibility_off</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon>lock</mat-icon>
            <span>lock</span>
          </div>
        </div>
      </div>

      <div class="icon-demo-card">
        <span class="icon-demo-card-title">Sizing</span>
        <div class="flex items-end gap-6 mt-3">
          <div class="icon-demo-item">
            <mat-icon class="w-4! h-4! text-[16px]!">home</mat-icon>
            <span>16px</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon class="w-5! h-5! text-[20px]!">home</mat-icon>
            <span>20px</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon>home</mat-icon>
            <span>24px (default)</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon class="w-8! h-8! text-[32px]!">home</mat-icon>
            <span>32px</span>
          </div>
          <div class="icon-demo-item">
            <mat-icon class="w-10! h-10! text-[40px]!">home</mat-icon>
            <span>40px</span>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════════════════════════════════ -->
  <!-- SHADOWS                                             -->
  <!-- ═══════════════════════════════════════════════════ -->
  <section id="shadows" class="sg-section">
    <h2 class="sg-section-title">Shadows</h2>
    <p class="sg-section-desc">Elevation levels using box-shadow tokens.</p>

    <div class="shadow-grid">
      <div class="shadow-demo">
        <div class="shadow-box" style="box-shadow: 0 2px 4px 0px rgba(0, 0, 0, 0.08)"></div>
        <span class="shadow-label">
          --shadow-card-sm
          <br />
          0 2px 4px 0 rgba(0,0,0,0.08)
        </span>
      </div>
      <div class="shadow-demo">
        <div class="shadow-box" style="box-shadow: 0 4px 8px 0px rgba(0, 0, 0, 0.15)"></div>
        <span class="shadow-label">
          --shadow-card
          <br />
          0 4px 8px 0 rgba(0,0,0,0.15)
        </span>
      </div>
      <div class="shadow-demo">
        <div class="shadow-box" style="box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2)"></div>
        <span class="shadow-label">
          --shadow-modal
          <br />
          0 8px 24px rgba(0,0,0,0.2)
        </span>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════════════════════════════════ -->
  <!-- BORDER RADIUS                                       -->
  <!-- ═══════════════════════════════════════════════════ -->
  <section id="radii" class="sg-section">
    <h2 class="sg-section-title">Border Radius</h2>
    <p class="sg-section-desc">Border radius tokens for consistent rounding.</p>

    <div class="token-grid">
      <div class="token-card">
        <div class="token-preview">
          <div style="width: 48px; height: 48px; background: #501283; border-radius: 1rem"></div>
        </div>
        <div class="token-info">
          <span class="token-name">--radius-card-sm</span>
          <span class="token-value">1rem (16px)</span>
        </div>
      </div>
      <div class="token-card">
        <div class="token-preview">
          <div style="width: 48px; height: 48px; background: #501283; border-radius: 1.5rem"></div>
        </div>
        <div class="token-info">
          <span class="token-name">--radius-card</span>
          <span class="token-value">1.5rem (24px)</span>
        </div>
      </div>
      <div class="token-card">
        <div class="token-preview">
          <div style="width: 48px; height: 48px; background: #501283; border-radius: 1rem"></div>
        </div>
        <div class="token-info">
          <span class="token-name">--radius-modal</span>
          <span class="token-value">1rem (16px)</span>
        </div>
      </div>
      <div class="token-card">
        <div class="token-preview">
          <div style="width: 48px; height: 48px; background: #501283; border-radius: 1.75rem"></div>
        </div>
        <div class="token-info">
          <span class="token-name">--radius-button</span>
          <span class="token-value">1.75rem (28px)</span>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════════════════════════════════ -->
  <!-- RELATED GALLERIES                                   -->
  <!-- ═══════════════════════════════════════════════════ -->
  <section id="galleries" class="sg-section">
    <h2 class="sg-section-title">Related Galleries</h2>
    <p class="sg-section-desc">Companion visual references for deeper component inspection.</p>

    <div class="gallery-links">
      <a class="gallery-link" href="/screenshots">
        <div>
          <span class="gallery-link-title">Screenshot Gallery</span>
          <span class="gallery-link-desc">Playwright-captured screenshots of all pages across viewports.</span>
        </div>
      </a>
      <a class="gallery-link" href="/modal-gallery">
        <div>
          <span class="gallery-link-title">Modal Gallery</span>
          <span class="gallery-link-desc">Visual catalog of all 40+ modal components with mock data.</span>
        </div>
      </a>
    </div>
  </section>
</div>
